<template>
  <div
    class="d-post-compare-images"
    :class="{ 'can-vote': canVote }"
    @click.stop
  >
    <div class="options-container-grid">
      <div class="option-container option-1">
        <div
          class="option-image"
          :class="{ 'selected': (activity.source.picked_option === 1) }"
          :disabled="isVoting"
          @click="voteForOption(1)"
        >
          <img
            :src="activity.source.option_1.medium.url"
            :title="activity.source.option_1_label"
          >

          <DuiIconTick class="image-selected-icon icon-white" />

          <button
            class="gallery-button button-translucent button-sm"
            @click.stop.prevent="openGallery(0)"
          >
            <DuiIconExpand class="icon-lg" />
          </button>
        </div>

        <div class="option-label">
          <strong>{{ activity.source.option_1_label }}</strong>
        </div>

        <DuiProgressBar
          color="yellow"
          :num-votes="activity.source.option_1_total"
          :value="option1Percentage"
        />
      </div>

      <div class="option-container option-2">
        <div
          class="option-image"
          :class="{ 'selected': (activity.source.picked_option === 2) }"
          :disabled="isVoting"
          @click="voteForOption(2)"
        >
          <img
            :src="activity.source.option_2.medium.url"
            :title="activity.source.option_2_label"
          >

          <DuiIconTick class="image-selected-icon icon-white" />

          <button
            class="gallery-button button-translucent button-sm"
            @click.stop.prevent="openGallery(1)"
          >
            <DuiIconExpand class="icon-lg" />
          </button>
        </div>

        <div class="option-label">
          <strong>{{ activity.source.option_2_label }}</strong>
        </div>

        <DuiProgressBar
          color="blue"
          :num-votes="activity.source.option_2_total"
          :value="option2Percentage"
        />
      </div>
    </div>

    <vue-gallery
      :id="`gallery-${ _uid }`"
      :images="galleryImages"
      :index="galleryIndex"
      @close="closeGallery"
    />
  </div>
</template>

<script>
import VueGallery from 'vue-gallery';

import pollPostMixin from '../poll-post-mixin';

export default {
  components: {
    VueGallery,
  },
  mixins: [
    pollPostMixin,
  ],
  data: () => ({
    galleryImages: null,
    galleryIndex: null,
  }),
  computed: {
    option1Percentage() {
      if (!this.activity.source.option_1_total) return 0;

      return Math.round(
        (this.activity.source.option_1_total /
        (this.activity.source.option_1_total + this.activity.source.option_2_total)
        ) * 100);
    },
    option2Percentage() {
      if (!this.activity.source.option_2_total) {
        return 0;
      }

      return Math.round(
        (this.activity.source.option_2_total /
        (this.activity.source.option_1_total + this.activity.source.option_2_total)
        ) * 100);
    },
  },
  mounted() {
    this.setupGallery();
  },
  methods: {
    // Gallery
    closeGallery() {
      this.$set(this, 'galleryIndex', null);
    },

    openGallery(index) {
      this.$set(this, 'galleryIndex', index);
    },

    setupGallery() {
      this.$set(this, 'galleryImages', [
        this.activity.source.option_1.large.url,
        this.activity.source.option_2.large.url,
      ]);
    },
    // END Gallery

    removeCurrentVote() {
      if (!this.activity.source.picked_option) {
        return;
      }

      if (this.activity.source.picked_option === 1) {
        this.$set(this.activity.source, 'option_1_total', this.activity.source.option_1_total - 1);
        return;
      }

      this.$set(this.activity.source, 'option_2_total', this.activity.source.option_2_total - 1);
    },

    sumVoteForOption(optionNumber) {
      if (optionNumber === 1) {
        this.$set(this.activity.source, 'option_1_total', this.activity.source.option_1_total + 1);
        return;
      }

      this.$set(this.activity.source, 'option_2_total', this.activity.source.option_2_total + 1);
    },

    async voteForOption(optionNumber) {
      if (this.activity.source.picked_option === optionNumber) {
        return;
      }

      const voteSuccessful = await this.pickPostOption(optionNumber);
      if (!voteSuccessful) {
        return;
      }

      this.removeCurrentVote();
      this.sumVoteForOption(optionNumber);
      this.$set(this.activity.source, 'picked_option', optionNumber);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@dui-styles/base";

.d-post-compare-images {
  cursor: default;
  pointer-events: none;

  &.can-vote {
    pointer-events: all;

    .option-image {
      @include transition((
        border-color,
        box-shadow
      ));

      &:hover {
        border-color: getColor($yellow);
        cursor: pointer;
      }
    }
  }

  .options-container-grid {
    display: grid;
    grid-column-gap: $_1em;
    grid-template-columns: 1fr 1fr;

    @include screenMoreThan(sm) {
      grid-column-gap: $_2em;
    }
  }

  .option-container {
    display: grid;
    grid-row-gap: $_1em;
    grid-template-rows: auto 1fr auto;

    .dui-progress-bar {
      align-self: end;
    }
  }

  .option-image {
    border: $border-width-base solid transparent;
    border-radius: $border-radius-lg;
    overflow: hidden;
    position: relative;

    &[disabled] {
      pointer-events: none;
      opacity: $disabled-opacity;
    }

    &.selected {
      border-color: getColor($yellow);
      box-shadow: $box-shadow-sm;

      &:before,
      .dui-icon.image-selected-icon {
        opacity: 1;
      }

      &:before {
        transform: rotate(45deg) scale(1) translate3d(0,0,0);
      }

      .dui-icon.image-selected-icon {
        transform: scale(1);
      }
    }

    &:before,
    .dui-icon.image-selected-icon {
      content: " ";
      display: block;
      opacity: 0;
      position: absolute;
      z-index: 1;

      @include transition((
        opacity,
        transform
      ));
    }

    &:before {
      background-color: getColor($yellow);
      height: $_10em;
      right: -$_5em;
      transform: rotate(45deg) scale(0.1) translate3d(0,0,0);
      top: -$_5em;
      width: $_10em;
    }

    .dui-icon.image-selected-icon {
      right: $_1em;
      top: $_1em;
      transform: scale(0.1);
      z-index: 2;
    }

    .gallery-button {
      bottom: $_2em;
      position: absolute;
      right: $_2em;
      z-index: 1;
    }

    img {
      display: flex;
      width: 100%;
    }
  }

  .option-label {
    align-self: end;
  }
}
</style>
